import { ATTACK_IDS } from './attack-ids'
import type { CoverageMatrix } from './coverage'
import type { CellHealth } from './coverage-health'

/**
 * Adversary lenses — phase 3 of docs/coverage-ops-requirements.md.
 *
 * "Are we covered for what attacks US?" A profile is a set of parent
 * technique NAMES; the matrix highlights them against tenant coverage and a
 * verdict row counts covered / degraded / gap for that adversary.
 *
 * Group profiles are CURATED from public ATT&CK group mappings, folded to
 * parent techniques — framework data, not tenant data, same standing as the
 * ATTACK_IDS map. They are a floor, not the actor's whole playbook (sub-
 * technique nuance folds up; mappings evolve between ATT&CK releases).
 * Industry profiles aggregate the techniques that dominate public incident
 * reporting for the vertical. A test pins every profile name to ATTACK_IDS,
 * so a renamed technique breaks the build, not the overlay.
 *
 * The watchlist is the ops escape hatch: paste technique names or T-ids out
 * of whatever report was read this morning; stored per scope.
 */

export interface AttackProfile {
  id: string
  label: string
  kind: 'group' | 'industry'
  /** One line of provenance, shown beside the verdict. */
  note: string
  techniques: string[]
}

export const ATTACK_PROFILES: AttackProfile[] = [
  {
    id: 'fin7',
    label: 'FIN7',
    kind: 'group',
    note: 'ATT&CK G0046 — financially motivated intrusions into payment and retail',
    techniques: [
      'Phishing', 'Valid Accounts', 'User Execution', 'Command and Scripting Interpreter',
      'Scheduled Task/Job', 'Ingress Tool Transfer', 'Obfuscated Files or Information',
      'Masquerading', 'Create or Modify System Process', 'OS Credential Dumping',
      'Remote Services', 'Screen Capture', 'Data from Local System',
      'Application Layer Protocol', 'Exfiltration Over C2 Channel',
    ],
  },
  {
    id: 'scattered-spider',
    label: 'Scattered Spider',
    kind: 'group',
    note: 'ATT&CK G1015 — help-desk social engineering into cloud and SaaS estates',
    techniques: [
      'Phishing', 'Multi-Factor Authentication Request Generation', 'Valid Accounts',
      'External Remote Services', 'Remote Access Software', 'Account Manipulation',
      'Modify Authentication Process', 'Steal Web Session Cookie', 'Cloud Service Discovery',
      'Data from Cloud Storage', 'Exfiltration Over Web Service', 'Impair Defenses',
      'Data Encrypted for Impact',
    ],
  },
  {
    id: 'apt29',
    label: 'APT29',
    kind: 'group',
    note: 'ATT&CK G0016 — state-sponsored espionage, identity and supply-chain tradecraft',
    techniques: [
      'Phishing', 'Supply Chain Compromise', 'Valid Accounts',
      'Command and Scripting Interpreter', 'Event Triggered Execution', 'Hide Artifacts',
      'Obfuscated Files or Information', 'OS Credential Dumping',
      'Steal or Forge Kerberos Tickets', 'Forge Web Credentials',
      'Use Alternate Authentication Material', 'Account Manipulation',
      'Ingress Tool Transfer', 'Application Layer Protocol', 'Exfiltration Over Web Service',
    ],
  },
  {
    id: 'apt28',
    label: 'APT28',
    kind: 'group',
    note: 'ATT&CK G0007 — credential-focused espionage against governments and NGOs',
    techniques: [
      'Phishing', 'Exploit Public-Facing Application', 'Valid Accounts', 'Brute Force',
      'OS Credential Dumping', 'Network Sniffing', 'Forced Authentication',
      'Command and Scripting Interpreter', 'Boot or Logon Autostart Execution',
      'Obfuscated Files or Information', 'Proxy', 'Data from Local System',
      'Exfiltration Over C2 Channel',
    ],
  },
  {
    id: 'lazarus',
    label: 'Lazarus Group',
    kind: 'group',
    note: 'ATT&CK G0032 — revenue and espionage operations, destructive when it suits',
    techniques: [
      'Phishing', 'Drive-by Compromise', 'User Execution',
      'Command and Scripting Interpreter', 'Obfuscated Files or Information',
      'Process Injection', 'Hijack Execution Flow', 'Masquerading',
      'System Information Discovery', 'Ingress Tool Transfer', 'Fallback Channels',
      'Application Layer Protocol', 'Exfiltration Over C2 Channel', 'Data Destruction',
    ],
  },
  {
    id: 'volt-typhoon',
    label: 'Volt Typhoon',
    kind: 'group',
    note: 'ATT&CK G1017 — living-off-the-land persistence in critical infrastructure',
    techniques: [
      'Exploit Public-Facing Application', 'External Remote Services', 'Valid Accounts',
      'Command and Scripting Interpreter', 'Windows Management Instrumentation',
      'OS Credential Dumping', 'Remote System Discovery',
      'System Network Configuration Discovery', 'Network Sniffing', 'Proxy',
      'Masquerading', 'Indicator Removal',
    ],
  },
  {
    id: 'ransomware-affiliate',
    label: 'Ransomware affiliate playbook',
    kind: 'group',
    note: 'the common affiliate kill chain across LockBit-era operations',
    techniques: [
      'Phishing', 'Exploit Public-Facing Application', 'External Remote Services',
      'Valid Accounts', 'Command and Scripting Interpreter', 'Impair Defenses',
      'Indicator Removal', 'OS Credential Dumping', 'Remote Services',
      'Lateral Tool Transfer', 'Service Stop', 'Inhibit System Recovery',
      'Exfiltration Over Web Service', 'Data Encrypted for Impact',
    ],
  },
  {
    id: 'industry-finance',
    label: 'Industry · finance',
    kind: 'industry',
    note: 'techniques dominating financial-sector incident reporting',
    techniques: [
      'Phishing', 'Valid Accounts', 'Brute Force',
      'Multi-Factor Authentication Request Generation', 'Adversary-in-the-Middle',
      'Steal Web Session Cookie', 'OS Credential Dumping', 'External Remote Services',
      'Financial Theft', 'Exfiltration Over Web Service', 'Data Encrypted for Impact',
    ],
  },
  {
    id: 'industry-healthcare',
    label: 'Industry · healthcare',
    kind: 'industry',
    note: 'ransomware-heavy: availability is the target',
    techniques: [
      'Phishing', 'Exploit Public-Facing Application', 'External Remote Services',
      'Valid Accounts', 'OS Credential Dumping', 'Remote Services', 'Impair Defenses',
      'Service Stop', 'Inhibit System Recovery', 'Data Encrypted for Impact',
      'Exfiltration Over C2 Channel',
    ],
  },
  {
    id: 'industry-manufacturing',
    label: 'Industry · manufacturing',
    kind: 'industry',
    note: 'edge exposure and lateral spread into OT-adjacent networks',
    techniques: [
      'Exploit Public-Facing Application', 'External Remote Services', 'Valid Accounts',
      'Command and Scripting Interpreter', 'Network Service Discovery',
      'System Network Configuration Discovery', 'Remote Services', 'Lateral Tool Transfer',
      'Impair Defenses', 'Inhibit System Recovery', 'Data Encrypted for Impact',
    ],
  },
  {
    id: 'industry-technology',
    label: 'Industry · technology / SaaS',
    kind: 'industry',
    note: 'identity and token abuse against cloud-native estates',
    techniques: [
      'Phishing', 'Valid Accounts', 'Multi-Factor Authentication Request Generation',
      'Steal Application Access Token', 'Steal Web Session Cookie', 'Forge Web Credentials',
      'Use Alternate Authentication Material', 'Account Manipulation',
      'Cloud Service Discovery', 'Data from Cloud Storage', 'Supply Chain Compromise',
      'Exfiltration Over Web Service',
    ],
  },
]

export function profileById(id: string | null): AttackProfile | null {
  return ATTACK_PROFILES.find((p) => p.id === id) ?? null
}

/* ---------------------------------------------------------------- watchlist */

export const WATCHLIST_ID = 'watchlist'

export function watchlistKey(clusterId: string, scopeId: string): string {
  return `bloo.coverage.watchlist.${clusterId}.${scopeId}`
}

export function loadWatchlist(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

export function saveWatchlist(key: string, text: string): void {
  try {
    window.localStorage.setItem(key, text)
  } catch {
    // Storage refused; the in-memory text still drives this visit's overlay.
  }
}

const idToName: Record<string, string> = Object.fromEntries(
  // Duplicate T-ids (e.g. T1530 twice, T1070 twice) resolve to the FIRST
  // name — reverse iteration keeps the earliest entry.
  Object.entries(ATTACK_IDS)
    .reverse()
    .map(([name, id]) => [id, name]),
)

const nameFold = new Map(Object.keys(ATTACK_IDS).map((n) => [n.toLowerCase(), n]))

export interface ParsedWatchlist {
  techniques: string[]
  /** Lines that matched nothing — surfaced, never silently dropped. */
  unknown: string[]
}

/**
 * One technique per line (or comma-separated): a parent T-id ("T1566"), a
 * sub-technique id (folded to its parent), or a technique name, any case.
 */
export function parseWatchlist(text: string): ParsedWatchlist {
  const techniques: string[] = []
  const unknown: string[] = []
  const seen = new Set<string>()
  for (const raw of text.split(/[\n,]/)) {
    const token = raw.trim()
    if (!token) continue
    const tid = /^t\d{4}(?:\.\d{3})?$/i.exec(token)?.[0]
    const name = tid
      ? idToName[`T${tid.slice(1, 5)}`]
      : nameFold.get(token.toLowerCase())
    if (!name) {
      unknown.push(token)
    } else if (!seen.has(name)) {
      seen.add(name)
      techniques.push(name)
    }
  }
  return { techniques, unknown }
}

/* --------------------------------------------------------------- assessment */

export interface ProfileAssessment {
  /** Techniques with tenant content whose armed detections still see data. */
  covered: string[]
  /** Content exists but every armed detection reads a dead stream. */
  degraded: string[]
  /** No tenant content at all — the adversary's free moves. */
  gaps: string[]
}

/**
 * The profile against the tenant: each profile technique is covered,
 * degraded, or a gap. Degraded counts as NOT covered on purpose — against a
 * named adversary, fiction coverage is the finding.
 */
export function assessProfile(
  techniques: string[],
  matrix: CoverageMatrix,
  health: Map<string, CellHealth>,
  healthKeyOf: (tactic: string, technique: string) => string,
): ProfileAssessment {
  const state = new Map<string, 'covered' | 'degraded'>()
  for (const t of matrix.tactics) {
    for (const q of t.techniques) {
      const degraded = health.get(healthKeyOf(t.name, q.name))?.degraded ?? false
      const prev = state.get(q.name)
      // A technique in several tactics is degraded only if EVERY sighting is.
      state.set(q.name, degraded && prev !== 'covered' ? 'degraded' : 'covered')
    }
  }
  const out: ProfileAssessment = { covered: [], degraded: [], gaps: [] }
  for (const name of techniques) {
    const s = state.get(name)
    if (s === 'covered') out.covered.push(name)
    else if (s === 'degraded') out.degraded.push(name)
    else out.gaps.push(name)
  }
  return out
}
